import React from 'react';
import { Badge } from '../../BasicComponents/Badge/Badge';
import { Spinner } from '../../BasicComponents/Spinner/Spinner';
import type { PaymentTerminalStatusProps, TerminalStatus } from './types';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  TerminalStatus,
  { label: string; description: string; color: string; badgeVariant: string }
> = {
  connected:    { label: 'Connected',    description: 'Terminal ready',              color: 'text-success-600 dark:text-success-400',  badgeVariant: 'active'    },
  connecting:   { label: 'Connecting',   description: 'Establishing connection…',    color: 'text-warning-600 dark:text-warning-400',  badgeVariant: 'pending'   },
  disconnected: { label: 'Disconnected', description: 'Terminal not reachable',       color: 'text-neutral-500 dark:text-neutral-400',  badgeVariant: 'inactive'  },
  processing:   { label: 'Processing',   description: 'Waiting for customer action…', color: 'text-brand-600 dark:text-brand-400',      badgeVariant: 'pendingSync' },
  error:        { label: 'Error',        description: 'Terminal error',               color: 'text-danger-600 dark:text-danger-400',    badgeVariant: 'error'     },
  manual:       { label: 'Not linked',   description: 'Take the payment on the device, then confirm here', color: 'text-neutral-600 dark:text-neutral-300', badgeVariant: 'inactive' },
};

// ─── PaymentTerminalStatus ────────────────────────────────────────────────────

export function PaymentTerminalStatus({
  status,
  terminalId,
  message,
  className,
}: PaymentTerminalStatusProps) {
  const config = STATUS_CONFIG[status];
  const isSpinning = status === 'connecting' || status === 'processing';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`Payment terminal: ${config.label}`}
      className={[
        'flex items-center gap-3 rounded-lg border px-4 py-3',
        status === 'error'
          ? 'border-danger-200 bg-danger-50 dark:bg-danger-900/20 dark:border-danger-700'
          : status === 'connected'
          ? 'border-success-200 bg-success-50 dark:bg-success-900/20 dark:border-success-700'
          : 'border-neutral-200 bg-white dark:bg-neutral-800 dark:border-neutral-600',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Icon / spinner */}
      {isSpinning ? (
        <Spinner size="sm" color={status === 'processing' ? 'brand-500' : 'neutral-400'} />
      ) : (
        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${
          status === 'connected'    ? 'bg-success-500' :
          status === 'disconnected' ? 'bg-neutral-400' :
          status === 'error'        ? 'bg-danger-500' :
          'bg-warning-500'
        }`} aria-hidden="true" />
      )}

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${config.color}`}>{config.label}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {message ?? config.description}
          {terminalId && ` · ${terminalId}`}
        </p>
      </div>

      <Badge variant={config.badgeVariant as any} size="sm">
        {config.label}
      </Badge>
    </div>
  );
}
